import React, { useEffect, useRef, useState } from 'react';
import { ExternalLink, Pencil, Trash2, Upload, UserPlus } from 'lucide-react';
import { API_ENDPOINTS } from '../../config/constants';
import { generateToken, TOKEN_HEADER } from '../../utils/tokenUtils';
import './MeetupGroup.css';

const PAGE_SIZE = 30;
const MEMBER_STATUSES = ['New', 'Cold', 'Warm', 'Customer', 'Lead'];
const EMPTY_MEMBER = {
  name: '', title: '', meetup_member_id: '', email: '', location: '',
  joined_group_on: '', last_visited_group_on: '', last_attended: '',
  all_time_rsvps: 0, all_time_yes_rsvps: 0, all_time_no_rsvps: 0,
  events_attended: 0, no_shows: 0, has_intro: false, has_photo: false,
  role: 'MEMBER', profile_url: '', status: 'New'
};

const DATE_FIELDS = ['joined_group_on', 'last_visited_group_on', 'last_attended'];
const NUMBER_FIELDS = [
  'all_time_rsvps', 'all_time_yes_rsvps', 'all_time_no_rsvps',
  'events_attended', 'no_shows'
];

function toFormMember(member) {
  const value = { ...EMPTY_MEMBER, ...member };
  DATE_FIELDS.forEach((field) => {
    value[field] = value[field] ? String(value[field]).slice(0, 10) : '';
  });
  return value;
}

function formatDate(value) {
  if (!value) return '-';
  const [year, month, day] = String(value).slice(0, 10).split('-').map(Number);
  if (!year || !month || !day) return '-';
  return new Intl.DateTimeFormat('en', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' })
    .format(new Date(Date.UTC(year, month - 1, day)));
}

function MeetupGroup() {
  const [members, setMembers] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pages: 0, total: 0 });
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [editingMember, setEditingMember] = useState(null);
  const [form, setForm] = useState(EMPTY_MEMBER);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [statusSavingId, setStatusSavingId] = useState(null);
  const fileInputRef = useRef(null);

  const fetchMembers = async (targetPage = page) => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ page: targetPage, limit: PAGE_SIZE });
      if (search.trim()) params.set('search', search.trim());
      if (status) params.set('status', status);
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/admin/meetup-members?${params}`, {
        headers: { [TOKEN_HEADER]: generateToken() }
      });
      const payload = await response.json();
      if (!response.ok || !payload.success) throw new Error(payload.message || 'Failed to load Meetup members');
      setMembers(payload.data);
      setPagination(payload.pagination);
      setPage(targetPage);
    } catch (fetchError) {
      setError(fetchError.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMembers(1);
    // Filters are submitted explicitly to avoid a request on every keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openForm = (member = null) => {
    setEditingMember(member);
    setForm(toFormMember(member || EMPTY_MEMBER));
  };

  const closeForm = () => {
    setEditingMember(null);
    setForm(EMPTY_MEMBER);
  };

  const updateField = (event) => {
    const { name, value, type, checked } = event.target;
    setForm((current) => ({
      ...current,
      [name]: type === 'checkbox' ? checked : NUMBER_FIELDS.includes(name) ? Number(value) : value
    }));
  };

  const saveMember = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    try {
      const response = await fetch(
        `${API_ENDPOINTS.BASE_URL}/admin/meetup-members${editingMember ? `/${editingMember.id}` : ''}`,
        {
          method: editingMember ? 'PUT' : 'POST',
          headers: { 'Content-Type': 'application/json', [TOKEN_HEADER]: generateToken() },
          body: JSON.stringify(form)
        }
      );
      const payload = await response.json();
      if (!response.ok || !payload.success) throw new Error(payload.message || 'Failed to save Meetup member');
      closeForm();
      await fetchMembers(editingMember ? page : 1);
    } catch (saveError) {
      setError(saveError.message);
    } finally {
      setSaving(false);
    }
  };

  const deleteMember = async (member) => {
    if (!window.confirm(`Delete ${member.name} from the Meetup group?`)) return;
    try {
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/admin/meetup-members/${member.id}`, {
        method: 'DELETE',
        headers: { [TOKEN_HEADER]: generateToken() }
      });
      const payload = await response.json();
      if (!response.ok || !payload.success) throw new Error(payload.message || 'Failed to delete Meetup member');
      await fetchMembers(members.length === 1 && page > 1 ? page - 1 : page);
    } catch (deleteError) {
      setError(deleteError.message);
    }
  };

  const changeMemberStatus = async (member, nextStatus) => {
    setStatusSavingId(member.id);
    setError('');
    try {
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/admin/meetup-members/${member.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', [TOKEN_HEADER]: generateToken() },
        body: JSON.stringify({ ...member, status: nextStatus })
      });
      const payload = await response.json();
      if (!response.ok || !payload.success) throw new Error(payload.message || 'Failed to update member status');
      setMembers((current) => current.map((item) => item.id === member.id ? payload.data : item));
    } catch (statusError) {
      setError(statusError.message);
    } finally {
      setStatusSavingId(null);
    }
  };

  const applyFilters = (event) => {
    event.preventDefault();
    fetchMembers(1);
  };

  const importCSV = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.csv')) {
      setError('Select a CSV file exported from Meetup.');
      return;
    }

    setImporting(true);
    setImportResult(null);
    setError('');
    try {
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/admin/meetup-members/import`, {
        method: 'POST',
        headers: { 'Content-Type': 'text/csv', [TOKEN_HEADER]: generateToken() },
        body: await file.text()
      });
      const payload = await response.json();
      if (!response.ok || !payload.success) throw new Error(payload.message || 'Failed to import Meetup members');
      setImportResult(payload.data);
      await fetchMembers(1);
    } catch (importError) {
      setError(importError.message);
    } finally {
      setImporting(false);
    }
  };

  return (
    <section className="meetup-group" aria-labelledby="meetup-group-title">
      <div className="meetup-toolbar">
        <div>
          <h2 id="meetup-group-title">MeetupGroup</h2>
          <p>{pagination.total} members</p>
        </div>
        <div className="meetup-toolbar-actions">
          <input ref={fileInputRef} type="file" accept=".csv,text/csv" onChange={importCSV} hidden />
          <button type="button" disabled={importing} onClick={() => fileInputRef.current?.click()}>
            <Upload size={16} aria-hidden="true" /> {importing ? 'Importing...' : 'Import CSV'}
          </button>
          <button className="meetup-primary" type="button" onClick={() => openForm()}>
            <UserPlus size={16} aria-hidden="true" /> Add member
          </button>
        </div>
      </div>

      <form className="meetup-filters" onSubmit={applyFilters}>
        <label>
          <span>Search</span>
          <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Name, email, or location" />
        </label>
        <label>
          <span>Status</span>
          <select value={status} onChange={(event) => setStatus(event.target.value)}>
            <option value="">All statuses</option>
            {MEMBER_STATUSES.map((memberStatus) => <option key={memberStatus} value={memberStatus}>{memberStatus}</option>)}
          </select>
        </label>
        <button type="submit">Apply</button>
      </form>

      {error && <div className="meetup-error" role="alert">{error}</div>}
      {importResult && (
        <div className="meetup-import-result" role="status">
          <strong>Import complete</strong>
          <span>{importResult.inserted} inserted</span>
          <span>{importResult.skipped} skipped by Member ID</span>
          {importResult.invalid > 0 && <span>{importResult.invalid} invalid</span>}
        </div>
      )}

      <div className="meetup-table-wrap">
        <table className="admin-table meetup-table">
          <thead>
            <tr>
              <th>Member</th><th>Contact</th><th>Activity</th><th>Engagement</th>
              <th>Status</th><th aria-label="Profile" /><th aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {!loading && members.map((member) => (
              <tr key={member.id}>
                <td>
                  <div className="meetup-member-cell">
                    <span className="meetup-avatar">{member.name?.trim()?.[0]?.toUpperCase() || '?'}</span>
                    <span><strong>{member.name}</strong><small>{member.title || member.role || `ID ${member.meetup_member_id}`}</small></span>
                  </div>
                </td>
                <td><span className="meetup-primary-text">{member.email || 'No email'}</span><small>{member.location || 'No location'}</small></td>
                <td><span className="meetup-primary-text">Joined {formatDate(member.joined_group_on)}</span><small>Last attended {formatDate(member.last_attended)}</small></td>
                <td><span className="meetup-primary-text">{member.events_attended || 0} events</span><small>{member.all_time_yes_rsvps || 0}/{member.all_time_rsvps || 0} yes RSVPs · {member.no_shows || 0} no-shows</small></td>
                <td>
                  <select
                    className={`meetup-status-select meetup-status-${String(member.status).toLowerCase()}`}
                    value={member.status || 'New'}
                    disabled={statusSavingId === member.id}
                    aria-label={`Status for ${member.name}`}
                    onChange={(event) => changeMemberStatus(member, event.target.value)}
                  >
                    {MEMBER_STATUSES.map((memberStatus) => <option key={memberStatus} value={memberStatus}>{memberStatus}</option>)}
                  </select>
                </td>
                <td>{member.profile_url ? <a className="meetup-icon-button" href={member.profile_url} target="_blank" rel="noreferrer" title="Open Meetup profile" aria-label={`Open ${member.name}'s Meetup profile`}><ExternalLink size={16} /></a> : null}</td>
                <td className="meetup-actions">
                  <button className="meetup-icon-button" type="button" title="Edit member" aria-label={`Edit ${member.name}`} onClick={() => openForm(member)}><Pencil size={16} /></button>
                  <button className="meetup-icon-button meetup-danger" type="button" title="Delete member" aria-label={`Delete ${member.name}`} onClick={() => deleteMember(member)}><Trash2 size={16} /></button>
                </td>
              </tr>
            ))}
            {!loading && members.length === 0 && <tr><td colSpan="7" className="meetup-empty">No members match these filters.</td></tr>}
            {loading && <tr><td colSpan="7" className="meetup-empty">Loading members...</td></tr>}
          </tbody>
        </table>
      </div>

      <div className="meetup-pagination" aria-label="Meetup members pagination">
        <button type="button" disabled={page <= 1 || loading} onClick={() => fetchMembers(page - 1)}>Previous</button>
        <span>Page {pagination.page} of {Math.max(pagination.pages, 1)}</span>
        <button type="button" disabled={page >= pagination.pages || loading} onClick={() => fetchMembers(page + 1)}>Next</button>
      </div>

      {(editingMember || form !== EMPTY_MEMBER) && (
        <div className="modal-overlay" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && closeForm()}>
          <div className="modal-content meetup-modal" role="dialog" aria-modal="true" aria-labelledby="meetup-form-title">
            <div className="meetup-modal-header">
              <h3 id="meetup-form-title">{editingMember ? 'Edit member' : 'Add member'}</h3>
              <button type="button" aria-label="Close" onClick={closeForm}>×</button>
            </div>
            <form onSubmit={saveMember}>
              <div className="meetup-form-grid">
                <label>Name<input required name="name" value={form.name} onChange={updateField} /></label>
                <label>Title<input name="title" value={form.title} onChange={updateField} /></label>
                <label>Member ID<input required type="number" min="1" name="meetup_member_id" value={form.meetup_member_id} onChange={updateField} /></label>
                <label>Email<input type="email" name="email" value={form.email} onChange={updateField} /></label>
                <label>Location<input name="location" value={form.location} onChange={updateField} /></label>
                <label>Role<input name="role" value={form.role} onChange={updateField} /></label>
                <label>Joined group on<input type="date" name="joined_group_on" value={form.joined_group_on} onChange={updateField} /></label>
                <label>Last visited group on<input type="date" name="last_visited_group_on" value={form.last_visited_group_on} onChange={updateField} /></label>
                <label>Last attended<input type="date" name="last_attended" value={form.last_attended} onChange={updateField} /></label>
                <label>All-time RSVPs<input type="number" min="0" name="all_time_rsvps" value={form.all_time_rsvps} onChange={updateField} /></label>
                <label>Yes RSVPs<input type="number" min="0" name="all_time_yes_rsvps" value={form.all_time_yes_rsvps} onChange={updateField} /></label>
                <label>No RSVPs<input type="number" min="0" name="all_time_no_rsvps" value={form.all_time_no_rsvps} onChange={updateField} /></label>
                <label>Events attended<input type="number" min="0" name="events_attended" value={form.events_attended} onChange={updateField} /></label>
                <label>No shows<input type="number" min="0" name="no_shows" value={form.no_shows} onChange={updateField} /></label>
                <label>Status<select name="status" value={form.status} onChange={updateField}>{MEMBER_STATUSES.map((memberStatus) => <option key={memberStatus} value={memberStatus}>{memberStatus}</option>)}</select></label>
                <label className="meetup-wide">Member profile URL<input type="url" name="profile_url" value={form.profile_url} onChange={updateField} /></label>
                <label className="meetup-check"><input type="checkbox" name="has_intro" checked={form.has_intro} onChange={updateField} /> Intro provided</label>
                <label className="meetup-check"><input type="checkbox" name="has_photo" checked={form.has_photo} onChange={updateField} /> Photo available</label>
              </div>
              <div className="meetup-form-actions">
                <button type="button" onClick={closeForm}>Cancel</button>
                <button className="meetup-primary" type="submit" disabled={saving}>{saving ? 'Saving...' : 'Save member'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </section>
  );
}

export default MeetupGroup;